import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Store, Users } from 'lucide-react';
import api, { apiError } from '../lib/api';

function AkunPengguna() {
  const [akun, setAkun] = useState(null);
  const [pilih, setPilih] = useState('');
  const [baru, setBaru] = useState('');
  const [ulang, setUlang] = useState('');
  const [menyimpan, setMenyimpan] = useState(false);

  useEffect(() => {
    api.get('/auth/users')
      .then((r) => {
        // Akun owner sengaja tidak muncul di sini. Untuk akun sendiri ada
        // menu Ganti Password yang tetap meminta password lama.
        const lain = (r.data || []).filter((u) => u.role !== 'owner');
        setAkun(lain);
        if (lain.length) setPilih(lain[0].id);
      })
      .catch(() => setAkun([]));
  }, []);

  const setel = async () => {
    if (baru.length < 8) return toast.error('Password baru minimal 8 karakter');
    if (baru !== ulang) return toast.error('Konfirmasi password tidak sama');
    setMenyimpan(true);
    try {
      const r = await api.post(`/auth/users/${pilih}/password`, { new_password: baru });
      toast.success(`Password ${r.data.email} berhasil disetel`);
      setBaru('');
      setUlang('');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setMenyimpan(false);
    }
  };

  if (akun === null || akun.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gold/20 shadow-soft p-6" data-testid="akun-pengguna-card">
      <div className="flex items-center gap-2 mb-2">
        <Users size={18} className="text-gold" />
        <h2 className="font-display text-xl text-ink">Akun Karyawan</h2>
      </div>
      <p className="text-sm text-mocha mb-5 leading-relaxed">
        Akun karyawan tidak punya menu Pengaturan, jadi dia tidak bisa mengganti
        passwordnya sendiri. Setel dari sini kalau karyawan lupa password, atau saat
        ada staf yang berhenti.
      </p>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Akun</label>
          <select
            data-testid="akun-pilih"
            value={pilih}
            onChange={(e) => setPilih(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
          >
            {akun.map((u) => (
              <option key={u.id} value={u.id}>{u.name ? `${u.name} — ${u.email}` : u.email}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password Baru</label>
          <input
            data-testid="akun-password-baru"
            type="password"
            value={baru}
            onChange={(e) => setBaru(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
          />
          {baru.length > 0 && baru.length < 8 && (
            <p className="text-xs text-danger mt-1" data-testid="akun-password-pendek">Minimal 8 karakter</p>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ulangi Password Baru</label>
          <input
            data-testid="akun-password-ulang"
            type="password"
            value={ulang}
            onChange={(e) => setUlang(e.target.value)}
            className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
          />
          {ulang.length > 0 && baru !== ulang && (
            <p className="text-xs text-danger mt-1" data-testid="akun-password-beda">Konfirmasi password tidak sama</p>
          )}
        </div>
        <button
          data-testid="akun-setel-password"
          onClick={setel}
          disabled={menyimpan || baru.length < 8 || baru !== ulang}
          className="rounded-full bg-emerald text-white px-6 py-3 text-sm font-semibold hover:bg-emerald-light transition-colors disabled:opacity-60"
        >
          {menyimpan ? 'Menyimpan…' : 'Setel Password'}
        </button>
        <p className="text-xs text-mocha leading-relaxed">
          Setelah disetel, password lama karyawan langsung tidak berlaku. Catat yang
          baru sebelum menutup halaman ini — tidak bisa dilihat lagi nanti.
        </p>
      </div>
    </div>
  );
}

export default function Settings() {
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    api.get('/settings').then((r) => setForm(r.data));
  }, []);

  if (!form) return <p className="text-sm text-muted-foreground py-16 text-center">Memuat pengaturan…</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/settings', form);
      toast.success('Profil bisnis tersimpan');
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePw = async () => {
    if (pw.new_password.length < 8) return toast.error('Password baru minimal 8 karakter');
    if (pw.new_password !== pw.confirm_password) return toast.error('Konfirmasi password tidak sama');
    setSavingPw(true);
    try {
      await api.post('/auth/change-password', { current_password: pw.current_password, new_password: pw.new_password });
      toast.success('Password berhasil diganti');
      setPw({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  const field = (testid, label, key, textarea = false, props = {}) => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      {textarea ? (
        <textarea
          data-testid={testid}
          value={form[key] || ''}
          onChange={(e) => set(key, e.target.value)}
          rows={2}
          className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
        />
      ) : (
        <input
          data-testid={testid}
          value={form[key] || ''}
          onChange={(e) => set(key, e.target.value)}
          className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
          {...props}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl" data-testid="settings-page">
      <h1 className="font-display text-3xl text-ink tracking-tight">Pengaturan</h1>

      <AkunPengguna />

      <div className="bg-white rounded-2xl border border-gold/20 shadow-soft p-6">
        <div className="flex items-center gap-2 mb-5">
          <Store size={18} className="text-gold" />
          <h2 className="font-display text-xl text-ink">Profil Bisnis</h2>
        </div>
        <div className="space-y-4">
          {field('settings-name-input', 'Nama Bisnis', 'business_name')}
          {field('settings-tagline-input', 'Tagline (tampil di nota)', 'tagline')}
          {field('settings-whatsapp-input', 'WhatsApp Bisnis', 'whatsapp', false, { type: 'tel', inputMode: 'numeric', autoComplete: 'tel' })}
          {field('settings-address-input', 'Alamat', 'address')}
          {field('settings-instagram-input', 'Akun Instagram (untuk pesan terima kasih)', 'instagram', false, { placeholder: 'deyrangallery' })}
          {field('settings-maps-input', 'Link ulasan Google Maps', 'google_maps_url', false, { placeholder: 'https://maps.app.goo.gl/...', inputMode: 'url' })}
          <p className="text-xs text-muted-foreground -mt-2">
            Google Maps → Deyran Gallery → Bagikan → Salin link. Dikosongkan = ajakan itu tidak muncul di pesan.
          </p>
          {field('settings-footer-input', 'Catatan Kaki Nota', 'receipt_footer', true)}
          <button
            data-testid="save-settings-button"
            onClick={saveProfile}
            disabled={savingProfile}
            className="rounded-full bg-emerald text-white px-6 py-3 text-sm font-semibold hover:bg-emerald-light transition-colors disabled:opacity-60"
          >
            {savingProfile ? 'Menyimpan…' : 'Simpan Profil'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gold/20 shadow-soft p-6">
        <div className="flex items-center gap-2 mb-5">
          <KeyRound size={18} className="text-gold" />
          <h2 className="font-display text-xl text-ink">Ganti Password</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password Lama</label>
            <input
              data-testid="current-password-input"
              type="password"
              value={pw.current_password}
              onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
              className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password Baru</label>
            <input
              data-testid="new-password-input"
              type="password"
              value={pw.new_password}
              onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
              className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Konfirmasi Password Baru</label>
            <input
              data-testid="confirm-password-input"
              type="password"
              value={pw.confirm_password}
              onChange={(e) => setPw((p) => ({ ...p, confirm_password: e.target.value }))}
              className="mt-1.5 w-full rounded-xl border border-input bg-ivory/50 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-emerald/40"
            />
            {pw.confirm_password && pw.new_password !== pw.confirm_password && (
              <p className="text-xs text-danger mt-1" data-testid="password-mismatch-hint">Konfirmasi password tidak sama</p>
            )}
          </div>
          <button
            data-testid="change-password-button"
            onClick={savePw}
            disabled={savingPw || (pw.confirm_password.length > 0 && pw.new_password !== pw.confirm_password)}
            className="rounded-full bg-emerald text-white px-6 py-3 text-sm font-semibold hover:bg-emerald-light transition-colors disabled:opacity-60"
          >
            {savingPw ? 'Menyimpan…' : 'Ganti Password'}
          </button>
        </div>
      </div>
    </div>
  );
}
